import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Table,
  LayerCard,
  Empty,
  Badge,
  Button,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { WifiHigh, HouseIcon, PlusIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useHasRole } from '../auth/useRoles'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type { WifiNetwork } from '@hub-wifi/shared'

export function WifiNetworksPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const isAdmin = useHasRole('admin')
  const navigate = useNavigate()
  const toasts = useKumoToastManager()
  const [networks, setNetworks] = useState<WifiNetwork[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const loadNetworks = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const data = await apiRequest<WifiNetwork[]>('/api/wifi-networks', { token })
      setNetworks(data)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently])

  useEffect(() => {
    void loadNetworks()
  }, [loadNetworks])

  async function handleDelete(network: WifiNetwork) {
    if (!window.confirm(t('wifiNetworks.confirmDelete'))) return

    try {
      const token = await getAccessTokenSilently()
      await apiRequest(`/api/wifi-networks/${network.id}`, {
        method: 'DELETE',
        token,
      })
      toasts.add({ variant: 'success', title: t('wifiNetworks.deleteSuccess') })
      setLoading(true)
      await loadNetworks()
    } catch (err) {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: err instanceof ApiError ? err.message : t('common.error'),
      })
    }
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('wifiNetworks.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('wifiNetworks.title')}
        description={t('wifiNetworks.description')}
      >
        {isAdmin && (
          <Button
            variant="primary"
            size="sm"
            icon={<PlusIcon size={16} />}
            onClick={() => navigate('/wifi-networks/new')}
          >
            {t('wifiNetworks.addNetwork')}
          </Button>
        )}
      </PageHeader>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error === 'GENERIC_ERROR' ? t('common.error') : error}
        </Banner>
      )}

      {!loading && !error && networks.length === 0 && (
        <Empty
          icon={<WifiHigh size={48} className="text-kumo-inactive" />}
          title={t('wifiNetworks.empty')}
        />
      )}

      {!loading && !error && networks.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>{t('wifiNetworks.name')}</Table.Head>
                  <Table.Head>{t('wifiNetworks.vlanId')}</Table.Head>
                  <Table.Head>{t('wifiNetworks.securityType')}</Table.Head>
                  <Table.Head>{t('wifiNetworks.networkType')}</Table.Head>
                  <Table.Head>{t('wifiNetworks.createdAt')}</Table.Head>
                  {isAdmin && <Table.Head>{t('wifiNetworks.actions')}</Table.Head>}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {networks.map((network) => (
                  <Table.Row key={network.id}>
                    <Table.Cell>{network.name}</Table.Cell>
                    <Table.Cell>{network.vlanId}</Table.Cell>
                    <Table.Cell>
                      <Badge variant="secondary">{network.securityType}</Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant="primary">{network.networkType}</Badge>
                    </Table.Cell>
                    <Table.Cell>{new Date(network.createdAt).toLocaleDateString()}</Table.Cell>
                    {isAdmin && (
                      <Table.Cell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<PencilSimpleIcon size={16} />}
                            onClick={() => navigate(`/wifi-networks/${network.id}/edit`)}
                          >
                            {t('wifiNetworks.edit')}
                          </Button>
                          <Button
                            variant="secondary-destructive"
                            size="sm"
                            onClick={() => void handleDelete(network)}
                          >
                            {t('wifiNetworks.delete')}
                          </Button>
                        </div>
                      </Table.Cell>
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}
